/**
 * What the live counters have "received" at a given instant.
 *
 * Pure, and shared by both figures ON PURPOSE. The bento shows the money raised and the number of
 * microdonations side by side, and the acceptance criterion for TEC-3832 is that they can never
 * contradict each other: one donation adds its amount AND one unit, in the same frame. Two components
 * each filtering the same array against their own `Date.now()` would satisfy that only by luck — they
 * read the clock a frame apart, so the count could show a donation the amount had not yet added.
 * Deriving both from one pass makes the invariant structural instead of coincidental.
 */

export type WindowDonation = { amountCents: number; timestamp: number };

export type ArrivedTotals = {
	/** Baseline plus everything that has arrived, in euros. */
	raisedEuros: number;
	/** Baseline plus everything that has arrived, as a count of donations. */
	donationCount: number;
};

/**
 * `now` is the wall clock in ms, or `0` for the deterministic first render: nothing has arrived yet, so
 * both server and browser produce exactly the baseline. That zero is load-bearing — the counter starting
 * anywhere else is how this application earned its history of React error 418.
 */
export function arrivedTotals(
	baseline: { totalAtWindowStartCents: number; totalAtWindowStartCount: number },
	donations: readonly WindowDonation[],
	now: number,
): ArrivedTotals {
	let cents = baseline.totalAtWindowStartCents;
	let count = baseline.totalAtWindowStartCount;

	for (const donation of donations) {
		if (donation.timestamp > now) continue;
		cents += donation.amountCents;
		count += 1;
	}

	return { raisedEuros: cents / 100, donationCount: count };
}
